// Button Click Tracking Utility
// Tracks clicks on competition calendar buttons for admin analytics
// Data is stored permanently in Firestore

import { collection, doc, setDoc, getDoc, getDocs, increment, serverTimestamp, arrayUnion, query, where } from "firebase/firestore";
import { db, auth } from "../firebase";

// Collection for storing button click stats
const CLICK_STATS_COLLECTION = "buttonClickStats";
const VISITOR_LOGS_COLLECTION = "visitorLogs";

/**
 * Generate a unique document ID for a competition button
 * @param {string} competitionId - The competition ID
 * @param {string} buttonType - The button type (Website, Registration, Schedule, Results, PrizeMoney, Playlist)
 * @returns {string} Document ID
 */
const getDocId = (competitionId, buttonType) => {
  return `${competitionId}_${buttonType.replace(/\s+/g, '')}`;
};

/**
 * Track a button click
 * Stores: total count, individual user click counts, timestamps
 * @param {string} competitionId - The competition ID
 * @param {string} competitionName - The competition name (for reference)
 * @param {string} buttonType - The button type
 * @param {string} url - The URL being clicked (optional)
 */
export const trackButtonClick = async (competitionId, competitionName, buttonType, url = null) => {
  try {
    const docId = getDocId(competitionId, buttonType);
    const docRef = doc(db, CLICK_STATS_COLLECTION, docId);
    
    // Get current user info
    const user = auth.currentUser;
    const userId = user ? user.uid : 'anonymous';
    const userEmail = user ? user.email : 'anonymous';
    const userName = user ? (user.displayName || user.email?.split('@')[0] || 'User') : 'Anonymous';
    
    // Create click entry with timestamp
    const clickEntry = {
      userId,
      userEmail,
      userName,
      timestamp: new Date().toISOString(),
      url: url || ''
    };
    
    // Write-only: the stats docs are admin-readable only, so visitors can't read the doc first.
    // increment()/arrayUnion() inside a merge create or update the doc atomically.
    // firstClick is no longer written (it needed a read); existing values are preserved by the merge.
    await setDoc(docRef, {
      competitionId,
      competitionName,
      buttonType,
      totalClicks: increment(1),
      userClicks: {
        [userId]: {
          userName,
          userEmail,
          count: increment(1),
          lastClick: clickEntry.timestamp
        }
      },
      recentClicks: arrayUnion(clickEntry),
      lastUpdated: serverTimestamp()
    }, { merge: true });

    console.log(`✅ Tracked click: ${buttonType} for ${competitionName}`);
    return true;
  } catch (error) {
    console.error("Error tracking button click:", error);
    return false;
  }
};

/**
 * Get click stats for a specific competition button
 * @param {string} competitionId - The competition ID
 * @param {string} buttonType - The button type
 * @returns {Object|null} Click stats object or null
 */
export const getButtonClickStats = async (competitionId, buttonType) => {
  try {
    const docId = getDocId(competitionId, buttonType);
    const docRef = doc(db, CLICK_STATS_COLLECTION, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.error("Error getting button click stats:", error);
    return null;
  }
};

/**
 * Get all click stats for a competition
 * @param {string} competitionId - The competition ID
 * @returns {Object} Map of buttonType -> stats
 */
export const getCompetitionClickStats = async (competitionId) => {
  try {
    const stats = {};
    const snapshot = await getDocs(collection(db, CLICK_STATS_COLLECTION));
    
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.competitionId === competitionId) {
        stats[data.buttonType] = data;
      }
    });
    
    return stats;
  } catch (error) {
    console.error("Error getting competition click stats:", error);
    return {};
  }
};

/**
 * Get all click stats for all competitions
 * @returns {Object} Map of competitionId -> { buttonType -> stats }
 */
export const getAllClickStats = async () => {
  try {
    const allStats = {};
    const snapshot = await getDocs(collection(db, CLICK_STATS_COLLECTION));
    
    snapshot.forEach(doc => {
      const data = doc.data();
      const compId = data.competitionId;
      const buttonType = data.buttonType;
      
      if (!allStats[compId]) {
        allStats[compId] = {};
      }
      allStats[compId][buttonType] = {
        totalClicks: data.totalClicks || 0,
        userClicks: data.userClicks || {},
        recentClicks: data.recentClicks || []
      };
    });
    
    return allStats;
  } catch (error) {
    console.error("Error getting all click stats:", error);
    return {};
  }
};

/**
 * Get historical click data from visitorLogs collection
 * This fetches the old tracking data before the new system was implemented
 * @returns {Object} Map of competitionName -> { buttonType -> stats }
 */
export const getHistoricalClickStats = async () => {
  try {
    const historicalStats = {};
    
    // Query visitorLogs for link_click interactions
    const logsQuery = query(
      collection(db, VISITOR_LOGS_COLLECTION),
      where("interactionType", "==", "link_click")
    );
    
    const snapshot = await getDocs(logsQuery);
    
    snapshot.forEach(doc => {
      const data = doc.data();
      const details = data.details || {};
      const competition = details.competition;
      const buttonType = details.button;
      
      // Skip if no competition or button info
      if (!competition || !buttonType) return;
      
      // Initialize competition entry
      if (!historicalStats[competition]) {
        historicalStats[competition] = {};
      }
      
      // Initialize button entry
      if (!historicalStats[competition][buttonType]) {
        historicalStats[competition][buttonType] = {
          totalClicks: 0,
          userClicks: {},
          recentClicks: []
        };
      }
      
      const stats = historicalStats[competition][buttonType];
      
      // Increment total clicks
      stats.totalClicks++;
      
      // Get user info
      const userId = data.userId || 'anonymous';
      const userEmail = data.userEmail || 'anonymous';
      const userName = data.userName || (userEmail !== 'anonymous' ? userEmail.split('@')[0] : 'Anonymous');
      
      // Get timestamp
      let timestamp;
      if (data.timestamp?.toDate) {
        timestamp = data.timestamp.toDate().toISOString();
      } else if (data.timestamp) {
        timestamp = new Date(data.timestamp).toISOString();
      } else {
        timestamp = new Date().toISOString();
      }
      
      // Update user clicks
      if (stats.userClicks[userId]) {
        stats.userClicks[userId].count++;
        // Update last click if this one is newer
        if (timestamp > stats.userClicks[userId].lastClick) {
          stats.userClicks[userId].lastClick = timestamp;
        }
        // Update first click if this one is older
        if (timestamp < stats.userClicks[userId].firstClick) {
          stats.userClicks[userId].firstClick = timestamp;
        }
      } else {
        stats.userClicks[userId] = {
          userName,
          userEmail,
          count: 1,
          firstClick: timestamp,
          lastClick: timestamp
        };
      }
      
      // Add to recent clicks
      stats.recentClicks.push({
        userId,
        userEmail,
        userName,
        timestamp,
        url: details.url || ''
      });
    });
    
    // Sort recent clicks by timestamp (newest first) and limit to 50
    Object.values(historicalStats).forEach(competition => {
      Object.values(competition).forEach(button => {
        button.recentClicks.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        button.recentClicks = button.recentClicks.slice(0, 50);
      });
    });
    
    console.log(`📊 Loaded historical click stats from visitorLogs`);
    return historicalStats;
  } catch (error) {
    console.error("Error getting historical click stats:", error);
    return {};
  }
};

/**
 * Get all click stats including historical data from visitorLogs
 * Merges new buttonClickStats with old visitorLogs data
 * @param {Array} competitions - Array of competition objects with id and name
 * @returns {Object} Map of competitionId -> { buttonType -> stats }
 */
export const getAllClickStatsWithHistory = async (competitions = []) => {
  try {
    // Get new tracking stats
    const newStats = await getAllClickStats();
    
    // Get historical stats from visitorLogs
    const historicalStats = await getHistoricalClickStats();
    
    // Create a map of competition name to ID for merging
    const nameToIdMap = {};
    competitions.forEach(comp => {
      if (comp.name && comp.id) {
        nameToIdMap[comp.name] = comp.id;
      }
    });
    
    // Merge historical stats into new stats
    Object.entries(historicalStats).forEach(([competitionName, buttons]) => {
      const competitionId = nameToIdMap[competitionName];
      
      // If we can map the name to an ID, merge the data
      if (competitionId) {
        if (!newStats[competitionId]) {
          newStats[competitionId] = {};
        }
        
        Object.entries(buttons).forEach(([buttonType, histStats]) => {
          if (!newStats[competitionId][buttonType]) {
            // No new stats for this button, use historical only
            newStats[competitionId][buttonType] = histStats;
          } else {
            // Merge historical with new
            const existing = newStats[competitionId][buttonType];
            
            // Add historical clicks to total
            existing.totalClicks += histStats.totalClicks;
            
            // Merge user clicks
            Object.entries(histStats.userClicks).forEach(([userId, histUser]) => {
              if (existing.userClicks[userId]) {
                // User exists in both, merge counts
                existing.userClicks[userId].count += histUser.count;
                // Keep earliest first click
                if (histUser.firstClick < existing.userClicks[userId].firstClick) {
                  existing.userClicks[userId].firstClick = histUser.firstClick;
                }
                // Keep latest last click
                if (histUser.lastClick > existing.userClicks[userId].lastClick) {
                  existing.userClicks[userId].lastClick = histUser.lastClick;
                }
              } else {
                existing.userClicks[userId] = histUser;
              }
            });
            
            // Merge recent clicks and sort
            existing.recentClicks = [...existing.recentClicks, ...histStats.recentClicks];
            existing.recentClicks.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            existing.recentClicks = existing.recentClicks.slice(0, 50);
          }
        });
      }
    });
    
    return newStats;
  } catch (error) {
    console.error("Error getting all click stats with history:", error);
    return {};
  }
};
